import { countryCodesDict } from "CONSTANTS";
import { useLoadingLineItemsDetails } from "api/wms/hooks";
import { LoadingPackageStatus, NormalizedLoadingPackageGroup, statusDict } from "api/wms/models";
import { Button } from "components/miloDesignSystem/atoms/button";
import { IconButton } from "components/miloDesignSystem/atoms/iconButton";
import { MdiQrCode } from "components/miloDesignSystem/atoms/icons/MdiQrCode";
import { Modal } from "components/miloDesignSystem/atoms/modal";
import { Tag } from "components/miloDesignSystem/atoms/tag";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { comfortableListUiSchema } from "components/miloDesignSystem/molecules/table/uiSchemas";
import { CommonError } from "components/utils";
import styles from "pages/wms/shared/Wms.module.css";
import { cx } from "utilities";
import { assertIsDefined } from "utilities/assertIsDefined";
import loadingIcon from "assets/images/trackS.svg";
import { useMemo } from "react";
import { Table } from "components/miloDesignSystem/molecules/table";
import { useLineItemsDetailsColumns } from "./useLineItemsDetailsColumns";
import { useDownloadLoadingLineItemPdf } from "../../hooks/useDownloadLoadingLineItemPdf";

interface Props {
  close: () => void;
  lineItemId: string;
}

export const loadingPackageStatusDict: Record<
  LoadingPackageStatus,
  { label: string; variant: "outline" | "warning" | "success" }
> = {
  ON_RAMP: { label: "Na rampie", variant: "outline" },
  PICKED_UP: { label: "Podniesiono", variant: "warning" },
  DURING_LOADING: { label: "W trakcie", variant: "warning" },
  LOADED: { label: "Wydano z magazynu", variant: "success" },
};

export const LineItemsDetailsModal = ({ close, lineItemId }: Props) => {
  const { data: lineItem, error, isLoading, isFetching } = useLoadingLineItemsDetails(lineItemId);
  const columns = useLineItemsDetailsColumns();
  const handleDownloadLoadingLineItemPdf = useDownloadLoadingLineItemPdf();
  const normalizedData: NormalizedLoadingPackageGroup[] = useMemo(() => {
    if (!lineItem) return [];
    return lineItem.packagesGroups.map(packageGroup => {
      const subRows: NormalizedLoadingPackageGroup[] = (packageGroup.packages || []).map(
        packageRow => {
          return {
            id: packageRow.id,
            name: packageRow.name,
            location: packageRow.location,
            pickedUpAt: packageRow.pickedUpAt,
            placeOnRampAt: packageRow.placeOnRampAt,
            status: packageRow.status,
            collaborator: packageRow.collaborator,
          } as NormalizedLoadingPackageGroup;
        },
      );
      return {
        id: packageGroup.id,
        name: packageGroup.name,
        location: null,
        pickedUpAt: null,
        placeOnRampAt: null,
        collaborator: null,
        status: null,
        subRows,
      } as NormalizedLoadingPackageGroup;
    });
  }, [lineItem]);

  if (isLoading) return null;

  if (error)
    return (
      <Modal
        close={close}
        isOpen
        width={750}
        title={
          <Typography fontSize="20" fontWeight="700">
            Szczegóły zamówienia
          </Typography>
        }
      >
        <div className="mt-3 py-5">
          <CommonError status={error._httpStatus_} />
        </div>
      </Modal>
    );

  assertIsDefined(lineItem);

  return (
    <Modal
      close={close}
      isOpen
      width={750}
      title={
        <Typography fontSize="20" fontWeight="700">
          Szczegóły zamówienia {lineItem.signature}
        </Typography>
      }
    >
      <div className={styles.lineItemContainer}>
        <div className={styles.lineItemTitle}>
          <Typography fontSize="26" fontWeight="700">
            {lineItem.loading.signature}
          </Typography>
          <div className="d-flex align-items-center gap-1">
            <img alt="loading" src={loadingIcon} style={{ height: 24, width: 24 }} />
            <Typography color="teal200" fontSize="20" fontWeight="700">
              Załadunek
            </Typography>
          </div>
          {lineItem.loading.ramp && (
            <Tag label={lineItem.loading.ramp.name} variant="deepPurple50" />
          )}
        </div>
        <div className={styles.lineItemSection}>
          <div className={styles.lineItemHeader}>
            <div className={styles.lineItemHeaderGeneral}>
              <Typography fontSize="16" fontWeight="700">
                {lineItem.signature}
              </Typography>
              <Tag
                label={statusDict[lineItem.status].label}
                variant={statusDict[lineItem.status].variant}
              />
              {lineItem.address.countryCode && (
                <div className="d-flex align-items-center gap-1">
                  <img alt="kraj" src={countryCodesDict[lineItem.address.countryCode]} />
                  <Typography fontSize="10" fontWeight="700">
                    {lineItem.address.countryCode}
                  </Typography>
                </div>
              )}
            </div>
            <div className={styles.lineItemHeaderBuyer}>
              <Typography fontSize="12" fontWeight="700">
                {lineItem.buyer.firstName || lineItem.buyer.lastName
                  ? `${lineItem.buyer.firstName} ${lineItem.buyer.lastName}`
                  : "---"}
              </Typography>
            </div>
            <IconButton
              icon={<MdiQrCode size="16" />}
              onClick={event => {
                event.stopPropagation();
                handleDownloadLoadingLineItemPdf(
                  lineItem.id,
                  lineItem.loading.id,
                  lineItem.signature,
                );
              }}
              variant="blackT"
            />
          </div>
          <div className={styles.tableContainer}>
            <Table<NormalizedLoadingPackageGroup>
              rows={normalizedData}
              columns={columns}
              isLoading={isLoading || isFetching}
              error={error}
              uiSchema={comfortableListUiSchema}
              overrides={row => {
                return {
                  row: {
                    className: cx({
                      [styles.finishedItem]: row.status === LoadingPackageStatus.LOADED,
                      [styles.inProgressItem]:
                        row.status === LoadingPackageStatus.DURING_LOADING ||
                        row.status === LoadingPackageStatus.PICKED_UP,
                    }),
                  },
                };
              }}
            />
          </div>
        </div>
        <div>
          <Button className="text-uppercase" onClick={close} size="medium" variant="transparent">
            Zamknij
          </Button>
        </div>
      </div>
    </Modal>
  );
};
